import { classNames } from "~/utils";
import { motion } from "framer-motion";
import boltGradientSvg from "~/assets/bolt-gradient.svg";
import React, { useMemo } from "react";
import {
  TriviaMultipleChoiceResult,
  TriviaResultEnum,
} from "~/graphql/codegen/graphql";
import { Button } from "@mindstonehq/ui";
import { useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

interface AnswerMCQProps {
  trivia?: {
    earnablePoints?: number | null;
    questions: {
      question: string;
      answers: { id: string; answer: string }[];
    }[];
  } | null;
  result?: {
    result: TriviaResultEnum;
    earnedTriviaPoints: number;
    answers: TriviaMultipleChoiceResult[];
  } | null;
  initialAnswerOrder: string[];
}

export function AnswerMCQ({
  trivia,
  result,
  initialAnswerOrder,
  children,
}: React.PropsWithChildren<AnswerMCQProps>) {
  const { t } = useLingui();
  const q = trivia?.questions[0];

  // Since the backend shuffles the initial answers on the question, we want the result shown to match the initial ordering
  const preserveInitialOrder = () => {
    if (!result?.answers) return [];

    // Create a map of the result answers for O(1) lookup
    const resultMap: { [key: string]: TriviaMultipleChoiceResult } = {};
    result.answers.forEach((a) => {
      resultMap[a.id] = a;
    });

    // If we have initialAnswerOrder use it, otherwise fallback to question order
    const orderToUse =
      initialAnswerOrder.length > 0
        ? initialAnswerOrder
        : q?.answers.map((a) => a.id) || [];

    return orderToUse
      .map((id) => resultMap[id])
      .filter((a): a is TriviaMultipleChoiceResult => a !== undefined);
  };

  const resultText = useMemo(() => {
    switch (result?.result) {
      case TriviaResultEnum.Almost:
        return t(msg`Near miss!`);
      case TriviaResultEnum.Correct:
        return t(msg`Great job!`);
      case TriviaResultEnum.Wrong:
        return t(msg`Whoops!`);
      case TriviaResultEnum.Timeout:
        return t(msg`Your time is up!`);
    }
    return "";
  }, [result?.result, t]);

  const ordered = preserveInitialOrder();
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.4 } }}
      exit={{ opacity: 0 }}
      className="flex flex-1 flex-col flex-1 space-y-5 justify-between items-center z-10 overflow-x-hidden overflow-y-auto"
    >
      <div className="flex flex-1 flex-col flex-1 space-y-5 justify-center items-center">
        <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between w-full space-y-6 sm:space-y-0">
          <h3>{resultText}</h3>
          <div
            className={classNames(
              "p-[1.5px] rounded-lg bg-gradient-to-br from-blue-300 via-purple-300 to-purple-400",
            )}
          >
            <div className="flex flex-row px-2.5 py-1 space-x-2 items-center rounded-lg bg-background text-muted-foreground">
              <img src={boltGradientSvg} alt={t(msg`points`)} className="h-4 w-4" />
              <span>
                <span className="font-bold">
                  {result?.earnedTriviaPoints || 0}
                </span>
                /{trivia?.earnablePoints || 0}
              </span>
            </div>
          </div>
        </div>
        <div className="text-lg font-semibold leading-7">{q?.question}</div>
        <div className="w-full flex flex-col flex-1 sm:flex-none items-center space-y-6 z-1">
          <div className="flex flex-col items-center w-full space-y-2">
            {ordered.map((a) => (
              <div
                key={a?.id}
                className={classNames(
                  "flex w-full items-center space-x-1 p-4 text-sm font-medium rounded-lg min-h-[55px]",
                  a?.result === TriviaResultEnum.Correct
                    ? "border-2 border-success"
                    : a.result === TriviaResultEnum.Almost &&
                        result?.result === TriviaResultEnum.Almost
                      ? "border-2 border-warning"
                      : a.result === TriviaResultEnum.Wrong &&
                          result?.result === TriviaResultEnum.Wrong
                        ? "border-2 border-destructive"
                        : "border",
                )}
              >
                {a?.answer}
              </div>
            ))}
          </div>
        </div>
      </div>
      {children || null}
    </motion.div>
  );
}
